/**
 * Single active payment-form registry.
 *
 * The PayZen Smart Form drives a single global `window.KR` object. If two
 * PayzenCardForm instances ever mounted with a token at once (e.g. the hidden
 * desktop EventBookingCard `hidden md:block` + the mobile modal one), they would
 * both register callbacks on that one KR and could submit simultaneously.
 *
 * This module grants a "render lock" to ONE instance at a time. Only the lock
 * holder renders the Krypton form (and registers onSubmit); others wait. In the
 * normal flow only the instance the user interacts with ever obtains a token, so
 * exactly one instance acquires — this is a safety net, not a behaviour change.
 *
 * Framework-agnostic so it is unit-testable with `node --test`.
 */

type Listener = () => void;

let holder: string | null = null;
const listeners = new Set<Listener>();

function notify() {
  // Copy to a snapshot so a listener that unsubscribes during notify is safe.
  Array.from(listeners).forEach((l) => l());
}

/** Try to take the lock. Returns true if this id holds it afterwards. */
export function acquirePaymentForm(id: string): boolean {
  if (holder === null || holder === id) {
    holder = id;
    return true;
  }
  return false;
}

/** Release the lock if held by `id`, letting waiters re-acquire. */
export function releasePaymentForm(id: string): void {
  if (holder === id) {
    holder = null;
    notify();
  }
}

/** Whether `id` may render (holds the lock or the lock is free). */
export function canRenderPaymentForm(id: string): boolean {
  return holder === null || holder === id;
}

export function getPaymentFormHolder(): string | null {
  return holder;
}

/** Subscribe to lock-release events; returns an unsubscribe fn. */
export function subscribePaymentForm(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only reset. */
export function __resetPaymentFormRegistry(): void {
  holder = null;
  listeners.clear();
}
